/**
 * Plugin nativo de reCAPTCHA v3 para Vue 2
 * Compatible con el proyecto sin dependencias externas
 */

class RecaptchaV3 {
  constructor(siteKey) {
    this.siteKey = siteKey;
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
  }

  // Cargar el script de reCAPTCHA
  loadScript() {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      if (this.isLoaded || window.grecaptcha) {
        this.isLoaded = true;
        return resolve();
      }

      if (this.isLoading) {
        return resolve();
      }

      this.isLoading = true;

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            this.isLoaded = true;
            this.isLoading = false;
            resolve();
          });
        } else {
          reject(new Error('reCAPTCHA failed to load'));
        }
      };

      script.onerror = () => {
        this.isLoading = false;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  // Ejecutar reCAPTCHA y obtener token
  async execute(action = 'submit') {
    if (!this.siteKey) {
      throw new Error('reCAPTCHA site key not configured');
    }

    await this.loadScript();

    return new Promise((resolve, reject) => {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        return reject(new Error('reCAPTCHA not available'));
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(this.siteKey, { action })
          .then(token => {
            if (token) {
              resolve(token);
            } else {
              reject(new Error('Failed to get reCAPTCHA token'));
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  // Verificar si reCAPTCHA está cargado
  async isReady() {
    try {
      await this.loadScript();
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Plugin para Vue 2
const RecaptchaV3Plugin = {
  install(Vue, options = {}) {
    const siteKey = options.siteKey || process.env.VUE_APP_RECAPTCHA_V3_SITE_KEY;
    
    if (!siteKey) {
      return;
    }

    const recaptcha = new RecaptchaV3(siteKey);

    // Agregar métodos globales a Vue
    Vue.prototype.$recaptcha = async function(action) {
      return await recaptcha.execute(action);
    };

    Vue.prototype.$recaptchaLoaded = async function() {
      return await recaptcha.isReady();
    };

    Vue.prototype.$recaptchaInstance = recaptcha;

    // Método helper para componentes
    Vue.mixin({
      methods: {
        async executeRecaptcha(action = 'submit') {
          return await this.$recaptcha(action);
        }
      }
    });
  }
};

export default RecaptchaV3Plugin; 